import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useCompanies } from "hooks/companies";
import { useToast } from "hooks/toast";
import api from "services/api";
import ApiHubBackend from "services/ApiHubBackend";
import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";
import Step1DadosEmbarque from "./components/Step1DadosEmbarque";
import Step2VeiculosSimulacao from "./components/Step2VeiculosSimulacao";
import Step3Transportadora from "./components/Step3Transportadora";
import SummaryPanel from "./components/SummaryPanel";
import SolicitarCotacaoModal from "./components/SolicitarCotacaoModal";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import "./components/novaCotacao.css";
import {
  MODO_CARGA,
  caixaVazia,
  caixasCompletas,
  caixasParaPayload,
  caixaValida,
  descreverCaixa,
  resumoTotais,
  totaisCaixas,
} from "./utils/caixas"; // PO1-3303
import { propostaEscolhidaDe } from "../ListagemCotacoes/agregarPropostas"; // PO1-2570
import { ordenarPropostas } from "./utils/ordenarPropostas"; // PO1-3915
import { deInputLocal, validarLimites } from "../../../utils/limitesColetaEntrega";
import { montarOpcoesTiposVeiculo } from "./utils/tiposVeiculo"; // PO1-3480
import obterUserNome from "utils/userNome"; // PO1-3488
import { cnpjCpfValido, montarDestinatario, montarEnderecoEntrega } from "../../../utils/destinatarioCotacao"; // PO1-3487

const CREDENCIAL =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbiIsImlhdCI6MTYxMzM5MDgwNywiZXhwIjoxOTI4NzUwODA3fQ.pYaKQjzeMk1VXqp1YnHIbhSwXMrzfuIWIOawoX9l4ag";

const STEPS = [
  { id: 1, label: "Dados do Embarque" },
  { id: 2, label: "Veículos e Simulação" },
  { id: 3, label: "Transportadora" },
];

// Leilão direto (13/10/2026, PO1-3084): "+ Novo leilão › Nova carga" abre esta
// tela com ?leilao=1. Só os dois primeiros passos: a transportadora sai do
// leilão, então não há escolha aqui; a simulação vira referência opcional.
const STEPS_LEILAO = [
  { id: 1, label: "Dados da Carga" },
  { id: 2, label: "Veículos e Referência" },
];

const STEP_META = {
  1: {
    eyebrow: "Preparação",
    title: "Dados do embarque",
    description:
      "Defina destino, remetente e informações-base para iniciar a simulação.",
  },
  2: {
    eyebrow: "Simulação",
    title: "Mix de veículos",
    description:
      "Monte a composição da carga e rode a simulação para descobrir as propostas disponíveis.",
  },
  3: {
    eyebrow: "Escolha final",
    title: "Seleção da transportadora",
    description:
      "Compare as opções retornadas e confirme a transportadora mais adequada para salvar a cotação.",
  },
};

const heroShellStyle = {
  ...autopilotCardStyle,
  display: "flex",
  flexDirection: "column",
  background: "var(--everlog-card-hero-bg)",
};

const SectionShell = ({
  className = "",
  eyebrow,
  title,
  description,
  actions = null,
  children,
}) => (
  <Card className={`card-everlog-shell ${className}`.trim()} style={heroShellStyle}>
    <CardHeader
      className={`wizard-shell__header${actions ? " wizard-shell__header--split" : ""}`}
      style={autopilotHeaderStyle}
    >
      <div>
        {eyebrow ? <div style={autopilotEyebrowStyle}>{eyebrow}</div> : null}
        {title ? <div style={autopilotTitleStyle}>{title}</div> : null}
        {description ? <p style={autopilotDescriptionStyle}>{description}</p> : null}
      </div>
      {actions ? <div className="wizard-shell__actions">{actions}</div> : null}
    </CardHeader>
    <CardBody className="wizard-shell__body" style={autopilotBodyStyle}>
      {children}
    </CardBody>
  </Card>
);

const emptyForm = {
  destinatario: "",
  cidade: "",
  uf: "",
  idCidadeDestino: null,
  cep: "",
  numeroNota: "",
  codigoPedido: "",
  valorNF: "",
  remetente: null,
  peso: "",
  volume: "",
  densidade: "",
  largura: "",
  altura: "",
  comprimento: "",
  observacoes: "",
  // PO1-3481: limites opcionais (valor do input datetime-local).
  coletarAte: "",
  entregarAte: "",
  // PO1-3487: destinatário da NF (destinatario = razão social) e
  // complemento do local de entrega (CEP/cidade/UF acima são a entrega).
  destCnpjCpf: "",
  destLogradouro: "",
  destNumero: "",
  destComplemento: "",
  destBairro: "",
  destCep: "",
  destCidade: "",
  destUf: "",
  entregaNomeLocal: "",
  entregaLogradouro: "",
  entregaNumero: "",
  entregaComplemento: "",
  entregaBairro: "",
};

const emptyVeiculo = (id) => ({
  id,
  tipoVeiculo: null,
  quantidade: 1,
  peso: "",
  freteSimulado: null,
  proposals: [],
});

const parseBR = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const cleaned = value
    .replace(/\s/g, "")
    .replace(/[R$]/gi, "")
    .replace(/[^\d.,-]/g, "");

  if (!cleaned) return 0;

  let normalized = cleaned;
  if (cleaned.includes(",")) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(".")) {
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const last = parts.pop();
      normalized = `${parts.join("")}.${last}`;
    }
  }

  normalized = normalized.replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const parseCurrencyToNumber = parseBR;

const buildRemetenteOptions = (items) => {
  if (!Array.isArray(items)) return [];

  const seen = new Set();

  return items.reduce((acc, item) => {
    const value = item?.embarcador_id;
    if (value == null || seen.has(String(value))) {
      return acc;
    }

    seen.add(String(value));
    acc.push({
      value,
      matrizId: item?.matriz_id ?? item?.matrizId ?? "",
      label: item?.embarcador_fantasia,
      cnpj:
        item?.embarcador_cnpj ||
        item?.cnpj ||
        item?.cnpjRemetente ||
        item?.cnpj_remetente ||
        "",
    });
    return acc;
  }, []);
};

const NovaCotacaoManual = () => {
  const history = useHistory();
  const modoLeilao = useRef(new URLSearchParams(history.location.search).get("leilao") === "1").current;
  const rotaVoltar = modoLeilao ? "/app/gobid/operacao/leiloes" : "/app/gobid/operacao/listagem-cotacoes";
  const { empresa, hasEmpresaSelection } = useCompanies();
  const empresaId = empresa?.value;
  const { addToast } = useToast();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(emptyForm);
  const [veiculos, setVeiculos] = useState([emptyVeiculo(1)]);
  const [nextId, setNextId] = useState(2);
  // PO1-3303 (H1): carga informada como total (campos únicos) ou por caixas.
  const [modoCarga, setModoCarga] = useState(MODO_CARGA.TOTAL);
  const [caixas, setCaixas] = useState([caixaVazia(1)]);
  const [nextCaixaId, setNextCaixaId] = useState(2);
  const [proposals, setProposals] = useState([]);
  // PO1-3200: propostas "+ Frete Spot" (transportadora da carteira com valor
  // negociado). Estado separado para sobreviver a uma nova simulação.
  const [spots, setSpots] = useState([]);
  // PO1-2905 (H4): transportadoras omitidas da simulação por vigência da tabela.
  const [omitidas, setOmitidas] = useState([]);
  const [transportadoraEscolhida, setTransportadoraEscolhida] = useState(null);
  // Indicadores ANTT (pill por transportadora) + Carbono (selo do embarque),
  // calculados no backend_hub após a simulação. null = ainda não calculado.
  const [indicador, setIndicador] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [remetentes, setRemetentes] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [usarCidadeSemCEP, setUsarCidadeSemCEP] = useState(false);
  const [estados, setEstados] = useState([]);
  // simulouSemPropostas marca quando o usuário simulou e o backend não devolveu
  // nenhuma transportadora — habilita o callout/CTA de "Solicitar à Transportadora".
  const [simulouSemPropostas, setSimulouSemPropostas] = useState(false);
  const [solicitarOpen, setSolicitarOpen] = useState(false);
  // PO1-2053: ao simular, captura cidade resolvida pelo backend pra usar no
  // POST /api/cotacao-spot/disparar (precisa de idCidadeDestino + UF).
  const [cidadeDestinoSimulada, setCidadeDestinoSimulada] = useState(null);
  const [ufDestinoSimulada, setUfDestinoSimulada] = useState(null);
  // PO1-2103: frete aproximado (mediana) — acionado SÓ pelo botão explícito
  // no estado "sem propostas", ao lado de "Solicitar à Transportadora".
  const [aproximadoCtx, setAproximadoCtx] = useState(null); // { embarques, idEmbarcador, idEmbarcadorRemetente }
  const [aproximadoLoading, setAproximadoLoading] = useState(false);
  const [aproximadoResultado, setAproximadoResultado] = useState(null);
  // cotacaoAproximada: marca que as propostas atuais vieram do frete
  // aproximado → salva embarque com aproximado:true (e valor editável).
  const [cotacaoAproximada, setCotacaoAproximada] = useState(false);
  // PO1-2769 — serviços adicionais (tipo + valor) somados ao frete total
  const [servicos, setServicos] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);

  useEffect(() => {
    if (!empresaId) return;
    // O parâmetro EmbarcadorParametro.UsarCidadeSemCEP virou redundante: o
    // backend (FreteService.buscarDestino) já aceita CEP OU idCidadeDestino+UF
    // pra qualquer embarcador. Agora a tela oferece os dois modos via toggle.
    Promise.all([
      api.get(`/embarcadores/${empresaId}`),
      api.get("/frete/tabelas/ativas/tipos-veiculo", {
        params: { idEmbarcador: empresaId },
      }),
      api.get("/frete/estados").catch(() => ({ data: [] })),
      // PO1-2769 — mesma rota de leitura já usada em NovaTabelaFrete
      api.get("/frete/tipos-servico").catch(() => ({ data: [] })),
      // PO1-3480: catálogo de tipos de veículo (ativos): o cadastro base + os
      // exclusivos deste cliente (PO1-3489). Sem ele, a lista fica só com os
      // que têm tabela, como antes.
      api
        .get("/frete/tipos-veiculo", { params: { idEmbarcador: empresaId } })
        .catch(() => ({ data: [] })),
    ])
      .then(([remRes, vtRes, estadosRes, tsRes, catRes]) => {
        const rawTs = Array.isArray(tsRes.data) ? tsRes.data : [];
        setTiposServico(
          rawTs
            .filter((t) => t && (t.id ?? t.Id) != null)
            .map((t) => ({
              value: t.id ?? t.Id,
              label: (t.nome ?? t.Nome) || String(t.id ?? t.Id),
            }))
        );
        setEstados(
          Array.isArray(estadosRes.data)
            ? estadosRes.data.map((e) => ({ value: e.uf, label: e.uf, id: e.id }))
            : []
        );
        const remOptions = buildRemetenteOptions(remRes.data);
        setRemetentes(remOptions);
        if (remOptions.length > 0) {
          setFormData((prev) => {
            const selectedOption = prev.remetente
              ? remOptions.find(
                  (option) =>
                    String(option.value) === String(prev.remetente?.value),
                )
              : null;

            return {
              ...prev,
              remetente: selectedOption || remOptions[0],
            };
          });
        }

        setVehicleTypes(
          montarOpcoesTiposVeiculo(
            Array.isArray(vtRes.data) ? vtRes.data : [],
            Array.isArray(catRes.data) ? catRes.data : []
          )
        );
      })
      .catch(() => {});
  }, [empresaId]); // eslint-disable-line

  useEffect(() => {
    if (hasEmpresaSelection) {
      return;
    }

    setStep(1);
    setFormData(emptyForm);
    setVeiculos([emptyVeiculo(1)]);
    setNextId(2);
    setProposals([]);
    setSpots([]);
    setOmitidas([]);
    setTransportadoraEscolhida(null);
    setSimulating(false);
    setSaving(false);
    setVehicleTypes([]);
    setRemetentes([]);
    setFormErrors({});
    setAproximadoCtx(null);
    setAproximadoResultado(null);
    setCotacaoAproximada(false);
    setServicos([]);
  }, [hasEmpresaSelection]);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    setSimulouSemPropostas(false);
    setAproximadoResultado(null);
    setAproximadoCtx(null);
    setCotacaoAproximada(false);
  }, []);

  const handleToggleModoDestino = useCallback((novoModoCidade) => {
    setUsarCidadeSemCEP(novoModoCidade);
    setFormData((prev) => ({
      ...prev,
      // Limpa o que pertence ao outro modo pra não vazar valor obsoleto na
      // simulação/save.
      ...(novoModoCidade
        ? { cep: "" }
        : { idCidadeDestino: null }),
    }));
    setFormErrors({});
  }, []);

  const handleAddVeiculo = useCallback(() => {
    setVeiculos((prev) => [...prev, emptyVeiculo(nextId)]);
    setNextId((n) => n + 1);
  }, [nextId]);

  const handleRemoveVeiculo = useCallback((id) => {
    setVeiculos((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((v) => v.id !== id);
    });
  }, []);

  // PO1-2769 — linhas de serviço (patch parcial: tipo e/ou valor)
  const handleAddServico = useCallback(() => {
    setServicos((prev) => [
      ...prev,
      { idTipoServico: null, tipoServico: "", valor: "" },
    ]);
  }, []);

  const handleRemoveServico = useCallback((idx) => {
    setServicos((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleServicoChange = useCallback((idx, patch) => {
    setServicos((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    );
  }, []);

  const handleVeiculoChange = useCallback((id, field, value) => {
    setVeiculos((prev) =>
      prev.map((v) => (v.id === id ? { ...v, [field]: value } : v))
    );
  }, []);

  // PO1-3303 (H1) — linhas de caixa (mesmo padrão das linhas de serviço).
  const handleAddCaixa = useCallback(() => {
    setCaixas((prev) => [...prev, caixaVazia(nextCaixaId)]);
    setNextCaixaId((n) => n + 1);
  }, [nextCaixaId]);

  const handleRemoveCaixa = useCallback((id) => {
    setCaixas((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : prev));
  }, []);

  const handleCaixaChange = useCallback((id, field, value) => {
    setCaixas((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  }, []);

  // Várias caixas é envio fracionado (decisão 1A do kickoff de 03/10/2026):
  // ao escolher "por caixas" o mix volta a um veículo. O mix é para carga total.
  const handleModoCargaChange = useCallback((modo) => {
    setModoCarga(modo);
    if (modo === MODO_CARGA.CAIXAS) {
      setVeiculos((prev) => (prev.length > 1 ? [prev[0]] : prev));
    }
  }, []);

  const porCaixas = modoCarga === MODO_CARGA.CAIXAS;
  const totais = useMemo(() => totaisCaixas(caixas), [caixas]);
  const caixasOk = porCaixas && caixasCompletas(caixas);
  const resumoCaixas = useMemo(() => resumoTotais(totais), [totais]);
  const caixasDescritas = useMemo(
    () =>
      caixas
        .filter(caixaValida)
        .map((c) => ({ id: c.id, descricao: descreverCaixa(c) })),
    [caixas]
  );
  // Peso de referência da carga: o total das caixas ou o digitado.
  const pesoCarga = porCaixas ? totais.pesoTotal : parseBR(formData.peso);

  const validateStep1 = useCallback(() => {
    const errors = {};
    if (usarCidadeSemCEP) {
      if (!formData.uf) errors.uf = "Campo obrigatório";
      if (!formData.idCidadeDestino) errors.cidade = "Selecione uma cidade";
    } else if (!formData.cep) {
      errors.cep = "Campo obrigatório";
    }
    if (!formData.valorNF) errors.valorNF = "Campo obrigatório";
    if (porCaixas) {
      if (!caixasOk) {
        errors.caixas = "Informe quantidade, peso e medidas de cada volume.";
      }
    } else if (!formData.peso) {
      errors.peso = "Campo obrigatório";
    }
    Object.assign(errors, validarLimites({ coletarAte: formData.coletarAte, entregarAte: formData.entregarAte }));
    if (!cnpjCpfValido(formData.destCnpjCpf)) errors.destCnpjCpf = "Informe 14 dígitos (CNPJ) ou 11 (CPF).";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, usarCidadeSemCEP, porCaixas, caixasOk]);

  const handleNext = useCallback(() => {
    if (step === 1 && !validateStep1()) return;
    setStep((s) => s + 1);
  }, [step, validateStep1]);

  // PO1-3200: lista exibida/gravada = simuladas + spot, por valor.
  // PO1-3915: as acima da capacidade do veículo vão para o fim.
  const todasPropostas = useMemo(
    () => ordenarPropostas([...proposals, ...spots]),
    [proposals, spots]
  );

  // Um spot por transportadora: incluir de novo a mesma substitui o valor.
  const handleAddSpot = useCallback((proposta) => {
    setSpots((prev) => [
      ...prev.filter((p) => p.idTransportadora !== proposta.idTransportadora),
      proposta,
    ]);
    setTransportadoraEscolhida(proposta);
  }, []);

  const handleRemoveSpot = useCallback((proposta) => {
    setSpots((prev) =>
      prev.filter((p) => p.idTransportadora !== proposta.idTransportadora)
    );
    setTransportadoraEscolhida((prev) =>
      prev && prev.fonte === "spot" && prev.idTransportadora === proposta.idTransportadora
        ? null
        : prev
    );
  }, []);

  const escolhidaEhSpot = transportadoraEscolhida?.fonte === "spot";

  // Indicadores ANTT/Carbono no backend_hub após a simulação. Complementar e
  // tolerante a falha. A origem (p/ a distância) é resolvida no backend pelo
  // cadastro do embarcador; aqui só enviamos destino, peso e propostas.
  const fetchIndicador = useCallback(
    async ({ idEmbarcador, destinoIdCidade, pesoKg, propostas }) => {
      if (
        !idEmbarcador ||
        destinoIdCidade == null ||
        !Array.isArray(propostas) ||
        propostas.length === 0
      ) {
        setIndicador(null);
        return;
      }
      try {
        const { data } = await api.post("/cotacao/indicadores", {
          idEmbarcador,
          embarques: [
            {
              ref: "nova",
              destino: { idCidade: Number(destinoIdCidade) },
              pesoKg: Number(pesoKg) || 0,
              propostas: propostas
                .filter((p) => p.idTransportadora != null)
                .map((p) => ({
                  idTransportadora: p.idTransportadora,
                  valorFrete: Number(p.valor) || 0,
                })),
            },
          ],
        });
        const emb =
          data && Array.isArray(data.embarques) ? data.embarques[0] : null;
        setIndicador(emb || null);
      } catch (err) {
        console.error("Erro ao calcular indicadores ANTT/carbono:", err);
        setIndicador(null);
      }
    },
    []
  );

  const handleSimular = useCallback(async () => {
    const destinoPronto = usarCidadeSemCEP
      ? formData.idCidadeDestino && formData.uf
      : formData.cep;
    const cargaPronta = porCaixas ? caixasOk : Boolean(formData.peso);
    if (!destinoPronto || !cargaPronta) {
      const missing = usarCidadeSemCEP ? "Cidade/UF" : "CEP";
      const carga = porCaixas ? "os volumes (quantidade, peso e medidas)" : "Peso";
      addToast({ place: "tr", type: "info", icon: "tim-icons icon-alert-circle-exc", title: "Atenção", description: `${missing} e ${carga} são obrigatórios para simular.` });
      return;
    }

    setSimulating(true);
    try {
      const valorNFNumber = parseCurrencyToNumber(formData.valorNF);
      const cepDigits = String(formData.cep || "").replace(/\D/g, "");

      // PO1-3303 (H1): por caixas, a cotação é UM envio (decisão 1A): só o
      // primeiro veículo entra, com a lista de caixas; peso, m³ e medidas vêm
      // da lista e o backend recalcula. Carga total segue como sempre.
      const veiculosCotados = porCaixas ? veiculos.slice(0, 1) : veiculos;
      const caixasPayload = porCaixas ? caixasParaPayload(caixas) : null;
      const embarques = veiculosCotados.map((v, i) => {
        const peso = porCaixas ? totais.pesoTotal : parseBR(v.peso || formData.peso);
        const base = {
          numero: `${formData.numeroNota || "SN"}-${i + 1}`,
          data: new Date().toISOString(),
          CNPJEmitente: formData.remetente?.cnpj || "",
          peso,
          ...(porCaixas ? { caixas: caixasPayload } : {}),
          // PO1-2903 (H2): o que alimenta a cubagem é o volume em m³, não o
          // campo "Densidade". Até aqui a tela só mandava densidade, que o
          // usuário quase nunca preenchia, e por isso toda tabela com fator de
          // cubagem devolvia R$ 0,00. As dimensões vão junto para os conectores
          // (PO1-2904); cada uma é opcional e independente.
          volume: porCaixas ? undefined : parseBR(formData.volume) || undefined,
          altura: porCaixas ? undefined : parseBR(formData.altura) || undefined,
          largura: porCaixas ? undefined : parseBR(formData.largura) || undefined,
          comprimento: porCaixas ? undefined : parseBR(formData.comprimento) || undefined,
          valor: i === 0 ? valorNFNumber : 0,
          idTipoVeiculo: v.tipoVeiculo?.value || null,
          quantidadeVeiculos: porCaixas ? 1 : Number(v.quantidade) || 1,
        };
        if (usarCidadeSemCEP) {
          return {
            ...base,
            idCidadeDestino: formData.idCidadeDestino,
            ufDestino: formData.uf,
          };
        }
        return { ...base, CEPDestinatario: cepDigits };
      });

      // idEmbarcador deve ser o embarcador pai (dono das tabelas de frete).
      // Quando um remetente é selecionado, seu matrizId aponta para o pai.
      // Se não houver remetente selecionado, usa empresa?.value diretamente.
      const idEmbarcadorPai =
        formData.remetente?.matrizId ||
        formData.remetente?.value ||
        empresa?.value;

      const payload = {
        credencial: CREDENCIAL,
        codigoModal: 1,
        quantidadeRetornos: 5,
        idEmbarcador: idEmbarcadorPai,
        idEmbarcadorRemetente: formData.remetente?.value || empresa?.value,
        embarques,
      };
      const response = await ApiHubBackend.post("/frete/consulta-frete", payload);

      const results = Array.isArray(response.data)
        ? response.data
        : [response.data];

      const updatedVeiculos = veiculos.map((v, i) => {
        const numero = `${formData.numeroNota || "SN"}-${i + 1}`;
        const result = results.find(
          (r) => r && String(r.numero) === String(numero)
        );
        const rawCotacoes = result?.freteCalculado || [];
        const props = rawCotacoes.map((f) => ({
          transportadora: f.transportadora,
          idTransportadora: f.idTransportadora != null ? f.idTransportadora : null, // PO1-2082
          valor: f.custoFrete != null ? Number(f.custoFrete) : 0,
          prazo: f.prazo ?? null,
          fonte: f.fonte || "tabela", // PO1-2550/H1: origem (tabela | api)
          // PO1-2902 (H1): peso que a faixa cobrou. Vem só nas propostas de
          // tabela (nas de API é null) e é o que o salvar grava em kg.
          pesoConsiderado: f.pesoConsiderado ?? null,
          pesoCubado: f.pesoCubado ?? null,
          volumeM3: f.volumeM3 ?? null,
          // PO1-3303 (H1): 'por-caixa' | 'pelo-total' | null (tabela, spot).
          modoCalculo: f.modoCalculo || null,
          // PO1-2570 (decisão 5A): identificação da proposta na API da transportadora.
          conectorKey: f.conectorKey ?? null,
          servico: f.servico != null ? String(f.servico) : null,
          protocolo: f.protocolo != null ? String(f.protocolo) : null,
          // PO1-3915: tabela/veículo que cotou. Linha sem veículo recebe uma
          // proposta por veículo da tabela (o nome já vem "Transp · Veículo").
          idTabela: f.idTabela ?? null,
          idTipoVeiculo: f.idTipoVeiculo ?? null,
          tipoVeiculo: f.tipoVeiculo != null ? String(f.tipoVeiculo) : null,
          capacidadeKg: f.capacidadeKg ?? null,
          acimaCapacidade: f.acimaCapacidade === true,
          // PO1-3917: adicional por km (km da rota, fonte e aviso quando falta).
          km: f.km ?? null,
          fonteKm: f.fonteKm != null ? String(f.fonteKm) : null,
          avisoKm: f.avisoKm != null ? String(f.avisoKm) : null,
        }));
        const melhor =
          props.length > 0
            ? props.reduce((m, p) => (p.valor < m.valor ? p : m), props[0])
            : null;
        return { ...v, proposals: props, freteSimulado: melhor?.valor ?? null };
      });

      setVeiculos(updatedVeiculos);

      // PO1-2905 (H4): transportadoras que têm tabela para a rota mas ficaram
      // de fora por vigência. Sem isso elas somem da lista sem explicação e o
      // usuário não sabe se a transportadora não atende ou se a tabela venceu.
      // Consolidado por transportadora, porque o bloco é do embarque inteiro.
      const omitidasPorTransportadora = new Map();
      results.forEach((r) => {
        (r?.omitidas || []).forEach((o) => {
          if (!omitidasPorTransportadora.has(o.idTransportadora)) {
            omitidasPorTransportadora.set(o.idTransportadora, o);
          }
        });
      });
      setOmitidas([...omitidasPorTransportadora.values()]);

      // Aggregate proposals by transportadora + fonte (PO1-2550/H1: uma mesma
      // transportadora pode aparecer por tabela E por API — mostrar as duas).
      const totals = {};
      updatedVeiculos.forEach((v) => {
        v.proposals.forEach((p) => {
          const fonte = p.fonte || "tabela";
          const chave = `${p.transportadora}||${fonte}`;
          if (!totals[chave]) {
            totals[chave] = {
              transportadora: p.transportadora,
              idTransportadora:
                p.idTransportadora != null ? p.idTransportadora : null, // PO1-2082
              valor: 0,
              prazo: null,
              fonte,
              // PO1-2902 (H1): peso da cotação por tabela. Somado entre os
              // veículos, como o valor, porque o salvar grava o total do
              // embarque. Fica null nas propostas de API.
              pesoConsiderado: null,
              pesoCubado: null,
              volumeM3: null,
              modoCalculo: null,
              // PO1-3915: tabela/veículo (primeiro não nulo) e capacidade
              // (OR entre as linhas do mix). PO1-3917: km é da rota, não
              // soma — fica o primeiro informado.
              idTabela: null,
              idTipoVeiculo: null,
              tipoVeiculo: null,
              capacidadeKg: null,
              acimaCapacidade: false,
              km: null,
              fonteKm: null,
              avisoKm: null,
            };
          }
          totals[chave].valor += p.valor;
          // PO1-3303: basta um embarque "pelo total" para a observação valer.
          if (p.modoCalculo && !totals[chave].modoCalculo) {
            totals[chave].modoCalculo = p.modoCalculo;
          }
          ["idTabela", "idTipoVeiculo", "tipoVeiculo", "capacidadeKg", "km", "fonteKm", "avisoKm"].forEach((campo) => {
            if (totals[chave][campo] == null && p[campo] != null) {
              totals[chave][campo] = p[campo];
            }
          });
          if (p.acimaCapacidade) totals[chave].acimaCapacidade = true;
          if (p.prazo != null) {
            totals[chave].prazo = Math.max(totals[chave].prazo ?? 0, p.prazo);
          }
          if (p.pesoConsiderado != null) {
            totals[chave].pesoConsiderado =
              (totals[chave].pesoConsiderado ?? 0) + p.pesoConsiderado;
          }
          if (p.pesoCubado != null) {
            totals[chave].pesoCubado =
              (totals[chave].pesoCubado ?? 0) + p.pesoCubado;
          }
          if (p.volumeM3 != null) {
            totals[chave].volumeM3 = (totals[chave].volumeM3 ?? 0) + p.volumeM3;
          }
        });
      });

      const aggregated = ordenarPropostas(Object.values(totals));

      if (aggregated.length === 0) {
        setProposals([]);
        setIndicador(null);
        setTransportadoraEscolhida(null);
        setSimulouSemPropostas(true);
        // PO1-2053: backend agora devolve cidadeDestino/ufDestino resolvidos
        // pra usar no botão "Solicitar à Transportadora".
        const primeiroResult = results.find(r => r && r.cidadeDestino);
        setCidadeDestinoSimulada(primeiroResult?.cidadeDestino || null);
        setUfDestinoSimulada(primeiroResult?.ufDestino || null);
        // PO1-2103: guarda a carga usada p/ o botão "Calcular frete
        // aproximado" reusar o MESMO payload (afrouxa só a cidade no backend).
        setAproximadoCtx({
          embarques,
          idEmbarcador: idEmbarcadorPai,
          idEmbarcadorRemetente: formData.remetente?.value || empresa?.value,
        });
        setAproximadoResultado(null);
        addToast({
          place: "tr",
          type: "info",
          icon: "tim-icons icon-alert-circle-exc",
          title: "Sem cotação automática",
          description: "Nenhuma transportadora retornou cotação para essa rota. Você pode solicitar manualmente.",
        });
      } else {
        setProposals(aggregated);
        setTransportadoraEscolhida(null);
        setSimulouSemPropostas(false);
        setCidadeDestinoSimulada(null);
        setUfDestinoSimulada(null);
        setAproximadoCtx(null);
        setAproximadoResultado(null);
        setCotacaoAproximada(false);
        // Indicadores ANTT/Carbono. No sucesso a cidadeDestinoSimulada é zerada,
        // então o destino vem do results (cidadeDestino resolvido pelo backend).
        const destinoIdIndic =
          (results.find((r) => r && r.cidadeDestino) || {}).cidadeDestino || null;
        fetchIndicador({
          idEmbarcador: idEmbarcadorPai,
          destinoIdCidade: destinoIdIndic,
          pesoKg: pesoCarga,
          propostas: aggregated,
        });
        addToast({ place: "tr", type: "success", icon: "tim-icons icon-check-2", title: "Simulação concluída", description: `${aggregated.length} transportadora(s) disponíveis.` });
      }
    } catch (err) {
      addToast({ place: "tr", type: "danger", icon: "tim-icons icon-alert-circle-exc", title: "Erro", description: "Falha ao simular fretes. Tente novamente." });
    } finally {
      setSimulating(false);
    }
  }, [formData, veiculos, empresa, addToast, usarCidadeSemCEP, fetchIndicador, porCaixas, caixasOk, caixas, totais, pesoCarga]);

  // PO1-2103: cálculo de frete aproximado (mediana). Reusa a MESMA carga da
  // simulação que voltou vazia; o backend (PO1-2102) afrouxa só a cidade.
  const handleCalcularAproximado = useCallback(async () => {
    if (!aproximadoCtx || !ufDestinoSimulada) {
      addToast({
        place: "tr",
        type: "info",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Frete aproximado",
        description: "Simule primeiro para ter a carga e o destino.",
      });
      return;
    }
    setAproximadoLoading(true);
    try {
      const response = await ApiHubBackend.post("/frete/aproximado", {
        credencial: CREDENCIAL,
        idEmbarcador: aproximadoCtx.idEmbarcador,
        idEmbarcadorRemetente: aproximadoCtx.idEmbarcadorRemetente,
        ufDestino: ufDestinoSimulada,
        embarques: aproximadoCtx.embarques,
      });
      const data = Array.isArray(response.data)
        ? response.data
        : [response.data];
      setAproximadoResultado(data);
      const temAlgum = data.some(
        (d) => d && Array.isArray(d.transportadoras) && d.transportadoras.length > 0
      );

      // Integra no fluxo padrão: o resultado aproximado vira `proposals`
      // (mesmo shape do fluxo normal) p/ poder selecionar no passo 3 e salvar.
      // Agrega por transportadora somando a mediana entre os embarques.
      const totaisAprox = {};
      data.forEach((d) => {
        (d?.transportadoras || []).forEach((t) => {
          if (!totaisAprox[t.transportadora]) {
            totaisAprox[t.transportadora] = {
              transportadora: t.transportadora,
              idTransportadora:
                t.idTransportadora != null ? t.idTransportadora : null,
              valor: 0,
              prazo: null,
            };
          }
          totaisAprox[t.transportadora].valor += Number(t.aproximado) || 0;
        });
      });
      const aggAprox = Object.values(totaisAprox).sort(
        (a, b) => a.valor - b.valor
      );
      if (aggAprox.length > 0) {
        setProposals(aggAprox);
        setTransportadoraEscolhida(null);
        setCotacaoAproximada(true);
      } else {
        setProposals([]);
        setCotacaoAproximada(false);
      }
      addToast({
        place: "tr",
        type: temAlgum ? "success" : "warning",
        icon: temAlgum ? "tim-icons icon-check-2" : "tim-icons icon-alert-circle-exc",
        title: "Frete aproximado",
        description: temAlgum
          ? "Estimativa calculada — sujeita a confirmação."
          : "Sem base suficiente para estimar. Solicite à transportadora.",
      });
    } catch (err) {
      const msg =
        err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : "Falha ao calcular o frete aproximado. Tente novamente.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Erro",
        description: msg,
      });
    } finally {
      setAproximadoLoading(false);
    }
  }, [aproximadoCtx, ufDestinoSimulada, addToast]);

  // PO1-3084 (épico PO1-3076): `opcoes.leiloar` = "Salvar e leiloar" — depois
  // de criar a cotação, vai direto à tela de Leilões com ela pré-carregada.
  const handleSalvar = useCallback(async (opcoes) => {
    const leiloar = Boolean(opcoes && opcoes.leiloar === true);
    // Leilão direto: a cotação nasce sem transportadora; quem define é o leilão.
    const semTransportadora = Boolean(opcoes && opcoes.semTransportadora === true);
    if (!transportadoraEscolhida && !semTransportadora) return;
    const escolhida = semTransportadora ? null : transportadoraEscolhida;
    // PO1-2769 — bloqueia salvar com linha de serviço incompleta (tipo sem
    // valor, ou valor sem tipo selecionado).
    const servicoIncompleto = servicos.some((s) => {
      const temTipo = s.idTipoServico != null;
      const temValor = parseBR(s.valor) > 0;
      return temTipo !== temValor;
    });
    if (servicoIncompleto) {
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: "Serviço incompleto",
        description:
          "Há um serviço com tipo ou valor não preenchido. Complete ou remova a linha antes de salvar.",
      });
      return;
    }
    setSaving(true);
    try {
      const valorNFNumber = parseCurrencyToNumber(formData.valorNF);
      const cepNumber = usarCidadeSemCEP
        ? null
        : Number(String(formData.cep).replace(/\D/g, "")) || null;
      const embarcadorIdPai =
        formData.remetente?.matrizId ||
        formData.remetente?.value ||
        empresa?.value;

      const payload = {
        idEmbarcador: embarcadorIdPai,
        dataEmbarque: new Date().toISOString(),
        // PO1-3481: limites de coleta/entrega ("" = sem limite).
        // PO1-3488: o histórico grava quem criou com o nome (o JWT só tem o e-mail).
        userNome: obterUserNome(),
        // PO1-3487
        destinatario: montarDestinatario(formData),
        enderecoEntrega: montarEnderecoEntrega(formData),
        coletarAte: deInputLocal(formData.coletarAte),
        entregarAte: deInputLocal(formData.entregarAte),
        // PO1-3272: vazio, o backend gera. O BR vai em campo próprio.
        numEmbarque: String(formData.numeroNota || "").trim() || undefined,
        codigoPedido: String(formData.codigoPedido || "").trim() || null,
        cliente: formData.destinatario,
        cidadeDestino: formData.cidade,
        ufDestino: formData.uf,
        idCidadeDestino: formData.idCidadeDestino || null,
        cepDestino: cepNumber,
        cnpjRemetente: formData.remetente?.cnpj || "",
        transportadora: escolhida ? escolhida.transportadora : null,
        transportadoraId:
          escolhida && escolhida.idTransportadora != null
            ? escolhida.idTransportadora
            : null, // PO1-2082
        propostaEscolhida: escolhida ? propostaEscolhidaDe(escolhida) : null, // PO1-2570
        valorFrete: escolhida ? escolhida.valor : null,
        statusEmbarque: "Pendente",
        // PO1-3200: escolhida por "+ Frete Spot" → origem Spot.
        origemCotacao: escolhida && escolhida.fonte === "spot" ? "Spot" : "Manual",
        // PO1-2103: flag de frete aproximado (não vale para o spot, que é valor negociado)
        aproximado: Boolean(escolhida) && cotacaoAproximada && escolhida.fonte !== "spot",
        pesoBruto: pesoCarga,
        valorNF: valorNFNumber,
        tipoFrete: "CIF",
        observacoes: formData.observacoes || "",
        cotacoes: todasPropostas.map((p) => ({
          transportadora: p.transportadora,
          idTransportadora: p.idTransportadora != null ? p.idTransportadora : null, // PO1-2082
          valor: p.valor,
          // PO1-3200: fonte (tabela/api/spot) passa a ser gravada; o Editar
          // Cotação e a listagem mostram o selo a partir dela.
          fonte: p.fonte || "tabela",
          ...(p.prazo != null ? { prazo: p.prazo } : {}),
          // PO1-3303: como a transportadora calculou com várias caixas.
          ...(p.modoCalculo ? { modoCalculo: p.modoCalculo } : {}),
          // PO1-2570 (decisão 5A)
          ...(p.conectorKey ? { conectorKey: p.conectorKey } : {}),
          ...(p.servico ? { servico: p.servico } : {}),
          ...(p.protocolo ? { protocolo: p.protocolo } : {}),
          // PO1-3915: veículo que a tabela cotou (só nas propostas que têm).
          ...(p.idTipoVeiculo != null ? { tipoVeiculoId: Number(p.idTipoVeiculo) } : {}),
          ...(p.tipoVeiculo ? { tipoVeiculo: p.tipoVeiculo } : {}),
        })),
        valor_total: escolhida ? escolhida.valor : null,
        // PO1-2769 — serviços adicionais; o valor de cada linha soma ao frete
        // no total exibido. valor_total segue sendo o frete (base).
        servicos: servicos
          .filter((s) => s.idTipoServico != null || parseBR(s.valor) > 0)
          .map((s) => ({
            idTipoServico: s.idTipoServico,
            tipoServico: s.tipoServico || null,
            valor: parseBR(s.valor) || 0,
          })),
        veiculosMix: veiculos.map((v) => ({
          tipoVeiculo: v.tipoVeiculo?.label || null,
          tipoVeiculoId: v.tipoVeiculo?.value || null,
          quantidade: porCaixas ? 1 : Number(v.quantidade) || 1,
          peso: porCaixas ? pesoCarga : parseBR(v.peso || formData.peso),
          freteSimulado: v.freteSimulado,
        })),
      };

      // PO1-3915: a linha do mix sem veículo recebe uma proposta por veículo
      // da tabela. Se a escolhida tem veículo e nenhuma linha informou um, o
      // embarque grava o veículo da escolhida (campos do schema do embarque).
      const mixSemVeiculo = !veiculos.some((v) => v.tipoVeiculo && v.tipoVeiculo.value != null);
      if (escolhida && escolhida.idTipoVeiculo != null && mixSemVeiculo) {
        payload.tipoVeiculoId = Number(escolhida.idTipoVeiculo);
        if (escolhida.tipoVeiculo) payload.tipoVeiculo = escolhida.tipoVeiculo;
      }

      // PO1-2903 (H2): o volume em m³ passa a ter campo próprio, e `pesoCubado`
      // passa a guardar QUILOS — o peso cubado da transportadora escolhida, que
      // a simulação devolve por proposta. Até aqui a tela gravava o m³ dentro de
      // `pesoCubado`, ou seja, um volume no campo chamado peso.
      if (formData.volume) payload.volume = parseBR(formData.volume);
      const pesoCubadoEscolhido = escolhida?.pesoCubado;
      if (pesoCubadoEscolhido != null) payload.pesoCubado = pesoCubadoEscolhido;
      if (formData.densidade) payload.densidade = parseBR(formData.densidade);
      if (formData.largura) payload.largura = parseBR(formData.largura);
      if (formData.altura) payload.altura = parseBR(formData.altura);
      if (formData.comprimento) payload.comprimento = parseBR(formData.comprimento);

      // PO1-3303 (H1): por caixas grava a lista, a soma dos volumes e o m³
      // total. Medidas só com um grupo de caixas iguais (com mais de um não
      // existe "medida total"; o backend grava 0 nos campos obrigatórios).
      if (porCaixas) {
        payload.caixas = caixasParaPayload(caixas);
        payload.qtdVolumes = totais.qtdVolumes;
        payload.volume = totais.volumeM3;
        delete payload.densidade;
        if (totais.grupos.length === 1) {
          payload.altura = totais.grupos[0].altura;
          payload.largura = totais.grupos[0].largura;
          payload.comprimento = totais.grupos[0].comprimento;
        } else {
          delete payload.altura;
          delete payload.largura;
          delete payload.comprimento;
        }
      }

      const { data: criada } = await api.post("/embarcadores/embarques", payload);
      // PO1-2863: o nº sequencial é o que o embarcador informa no ERP (NF-e de
      // remessa) — precisa aparecer já na confirmação.
      const numero = criada && criada.numeroCotacao != null ? criada.numeroCotacao : null;
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: numero != null ? `Cotação nº ${numero} salva` : "Cotação salva",
        description: numero != null
          ? `Cotação nº ${numero} criada com status Pendente.`
          : "Cotação criada com status Pendente.",
      });
      // PO1-3084: "Salvar e leiloar" segue para a tela de Leilões com a cotação.
      if (leiloar && criada && criada._id) {
        history.push(`/app/gobid/operacao/leiloes?cotacao=${criada._id}`);
        return;
      }
      // state.fresh → a listagem faz a 1ª carga sem cache p/ a cotação
      // recém-criada aparecer na hora (sem esperar o TTL de 3 min).
      history.push({ pathname: "/app/gobid/operacao/listagem-cotacoes", state: { fresh: true } });
    } catch (err) {
      const resp = err.response && err.response.data;
      let description;
      if (resp && typeof resp.error === "string") {
        description = resp.error;
      } else if (resp && resp.error && typeof resp.error === "object") {
        description = Object.values(resp.error).filter(Boolean).join(" · ")
          || "Alguns campos estão inválidos. Revise o formulário.";
      } else {
        description = "Falha ao salvar cotação. Tente novamente.";
      }
      addToast({ place: "tr", type: "danger", icon: "tim-icons icon-alert-circle-exc", title: "Erro ao salvar", description });
    } finally {
      setSaving(false);
    }
  }, [transportadoraEscolhida, formData, todasPropostas, veiculos, servicos, empresa, addToast, history, usarCidadeSemCEP, cotacaoAproximada, porCaixas, caixas, totais, pesoCarga]);

  // PO1-2053: rotaLabel pra display no modal de cotação spot.
  const rotaLabelSimulada = useMemo(() => {
    if (!cidadeDestinoSimulada) return null;
    const cidade = formData.cidade || "";
    const uf = ufDestinoSimulada || formData.uf || "";
    if (cidade && uf) return `${cidade}/${uf}`;
    if (uf) return uf;
    return null;
  }, [cidadeDestinoSimulada, ufDestinoSimulada, formData.cidade, formData.uf]);

  // PO1-2769 — soma dos serviços entra no total exibido (frete + serviços)
  const somaServicos = useMemo(
    () => servicos.reduce((acc, s) => acc + (parseBR(s.valor) || 0), 0),
    [servicos]
  );

  const freteTotal = useMemo(() => {
    const base = transportadoraEscolhida
      ? transportadoraEscolhida.valor
      : todasPropostas.length > 0
        ? todasPropostas[0].valor
        : null;
    if (base == null) return null;
    return base + somaServicos;
  }, [transportadoraEscolhida, todasPropostas, somaServicos]);

  const canGoNext = useMemo(() => {
    if (step === 1) {
      const destinoOk = usarCidadeSemCEP
        ? formData.uf && formData.idCidadeDestino
        : formData.cep;
      return destinoOk && formData.valorNF && (porCaixas ? caixasOk : formData.peso);
    }
    // PO1-3200: simulou e ninguém retornou → segue ao passo 3 para o "+ Frete Spot".
    if (step === 2) return todasPropostas.length > 0 || simulouSemPropostas;
    return false;
  }, [step, formData, todasPropostas, simulouSemPropostas, usarCidadeSemCEP, porCaixas, caixasOk]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content nova-cotacao-wizard wizard-page">
        <SpecificEmpresaRequiredNotice />
      </div>
    );
  }

  const currentStepMeta = STEP_META[step] || STEP_META[1];

  return (
    <div className="content nova-cotacao-wizard wizard-page">
      <Card className="card-everlog-shell wizard-hero" style={heroShellStyle}>
        <CardBody className="wizard-hero__body" style={autopilotBodyStyle}>
          <div className="wizard-hero__intro">
            <span className="wizard-hero__eyebrow">{modoLeilao ? "GoBID · Leilão" : "Negociação"}</span>
            <h2 className="wizard-hero__title">{modoLeilao ? "Novo leilão: dados da carga" : "Nova cotação manual"}</h2>
            <p className="wizard-hero__description">
              {modoLeilao
                ? "Preencha a carga. Ao salvar, o Hub cria a cotação (ela aparece em Minhas Cotações) e abre o leilão. A simulação de frete é opcional e serve de referência para o preço-alvo; quem define a transportadora é o leilão."
                : "Construa a cotação em três etapas, com resumo lateral e validação progressiva até a escolha final da transportadora."}
            </p>
          </div>

          <div className="wizard-hero__actions">
            <Button
              className="btn-sm"
              style={autopilotSecondaryButtonStyle}
              onClick={() => history.push(rotaVoltar)}
            >
              {modoLeilao ? "Voltar aos leilões" : "Voltar à listagem"}
            </Button>
          </div>

          <div className="wizard-progress-shell">
            <div className="wizard-stepper">
              {(modoLeilao ? STEPS_LEILAO : STEPS).map((s, i, lista) => (
                <React.Fragment key={s.id}>
                  <div
                    className={`wizard-step${step === s.id ? " active" : ""}${step > s.id ? " done" : ""}`}
                  >
                    <div className="wizard-step-circle">
                      {step > s.id ? "✓" : s.id}
                    </div>
                    <span className="wizard-step-label">{s.label}</span>
                  </div>
                  {i < lista.length - 1 && (
                    <div
                      className={`wizard-step-line${step > s.id ? " done" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Body */}
      <Row className="wizard-body">
        <Col lg="8">
          <SectionShell
            className="wizard-main-shell"
            eyebrow={currentStepMeta.eyebrow}
            title={currentStepMeta.title}
            description={currentStepMeta.description}
          >
            {step === 1 && (
              <Step1DadosEmbarque
                formData={formData}
                onChange={handleFormChange}
                remetentes={remetentes}
                errors={formErrors}
                usarCidadeSemCEP={usarCidadeSemCEP}
                onToggleModoDestino={handleToggleModoDestino}
                estados={estados}
                modoCarga={modoCarga}
                onModoCargaChange={handleModoCargaChange}
                caixas={caixas}
                onAddCaixa={handleAddCaixa}
                onRemoveCaixa={handleRemoveCaixa}
                onCaixaChange={handleCaixaChange}
                resumoCaixas={resumoCaixas}
              />
            )}
            {step === 2 && (
              <Step2VeiculosSimulacao
                porCaixas={porCaixas}
                pesoReferencia={porCaixas ? totais.pesoTotal : null}
                formData={formData}
                veiculos={veiculos}
                vehicleTypes={vehicleTypes}
                simulating={simulating}
                onAddVeiculo={handleAddVeiculo}
                onRemoveVeiculo={handleRemoveVeiculo}
                onVeiculoChange={handleVeiculoChange}
                onSimular={handleSimular}
                servicos={servicos}
                tiposServico={tiposServico}
                onAddServico={handleAddServico}
                onRemoveServico={handleRemoveServico}
                onServicoChange={handleServicoChange}
                simulouSemPropostas={simulouSemPropostas}
                onSolicitarTransportadora={() => setSolicitarOpen(true)}
                onCalcularAproximado={handleCalcularAproximado}
                aproximadoLoading={aproximadoLoading}
                aproximadoResultado={aproximadoResultado}
              />
            )}
            {step === 3 && (
              <Step3Transportadora
                proposals={todasPropostas}
                indicador={indicador}
                transportadoraEscolhida={transportadoraEscolhida}
                onSelectTransportadora={setTransportadoraEscolhida}
                aproximado={cotacaoAproximada && !escolhidaEhSpot}
                omitidas={omitidas}
                idEmbarcador={
                  formData.remetente?.matrizId ||
                  formData.remetente?.value ||
                  empresa?.value
                }
                onAddSpot={handleAddSpot}
                onRemoveSpot={handleRemoveSpot}
                onValorChange={(raw) => {
                  const n = parseBR(raw);
                  if (Number.isFinite(n) && n > 0) {
                    setTransportadoraEscolhida((prev) =>
                      prev ? { ...prev, valor: n } : prev
                    );
                  }
                }}
              />
            )}
          </SectionShell>
        </Col>
        <Col lg="4">
          <SummaryPanel
            step={step}
            formData={formData}
            veiculos={veiculos}
            proposals={todasPropostas}
            transportadoraEscolhida={transportadoraEscolhida}
            freteTotal={freteTotal}
            servicos={servicos}
            somaServicos={somaServicos}
            porCaixas={porCaixas}
            caixas={caixasDescritas}
            resumoCaixas={resumoCaixas}
            pesoReferencia={porCaixas ? totais.pesoTotal : null}
          />
        </Col>
      </Row>

      {/* Footer */}
      <Card className="card-everlog-shell wizard-footer-shell" style={heroShellStyle}>
        <CardBody className="wizard-footer-shell__body" style={autopilotBodyStyle}>
          <div className="wizard-footer">
            <Button
              color="secondary"
              outline
              className="wizard-footer-btn"
              onClick={() => history.push(rotaVoltar)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <div className="wizard-footer-right">
              {step > 1 && (
                <Button
                  color="secondary"
                  outline
                  className="wizard-footer-btn"
                  onClick={() => setStep((s) => s - 1)}
                  disabled={simulating || saving}
                >
                  Voltar
                </Button>
              )}
              {modoLeilao && step === 2 ? (
                <Button
                  color="primary"
                  className="wizard-footer-btn"
                  style={autopilotSecondaryButtonStyle}
                  onClick={() => handleSalvar({ leiloar: true, semTransportadora: true })}
                  disabled={saving || simulating}
                  title="Cria a cotação (Pendente, sem transportadora) e abre o leilão com ela"
                >
                  {saving ? "Salvando..." : "Salvar e abrir leilão"}
                </Button>
              ) : step < 3 ? (
                <Button
                  color="primary"
                  className="wizard-footer-btn"
                  style={autopilotSecondaryButtonStyle}
                  onClick={handleNext}
                  disabled={!canGoNext || simulating}
                >
                  Próximo
                </Button>
              ) : (
                <>
                  <Button
                    color="info"
                    className="wizard-footer-btn"
                    style={autopilotSecondaryButtonStyle}
                    onClick={() => handleSalvar({ leiloar: true })}
                    disabled={!transportadoraEscolhida || saving}
                    title="Salva a cotação e abre o leilão reverso de frete com ela"
                  >
                    {saving ? "Salvando..." : "Salvar e leiloar"}
                  </Button>
                  <Button
                    color="success"
                    className="wizard-footer-btn"
                    style={autopilotSecondaryButtonStyle}
                    onClick={() => handleSalvar()}
                    disabled={!transportadoraEscolhida || saving}
                  >
                    {saving ? "Salvando..." : "Salvar Cotação"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <SolicitarCotacaoModal
        open={solicitarOpen}
        onClose={() => setSolicitarOpen(false)}
        idEmbarcador={
          formData.remetente?.matrizId
          || formData.remetente?.value
          || empresa?.value
        }
        idCidadeDestino={cidadeDestinoSimulada}
        rotaLabel={rotaLabelSimulada}
        onSent={(data) => {
          const qtd = data?.transportadorasNotificadas?.length || 0;
          const semEmail = data?.transportadorasSemEmail || 0;
          if (qtd > 0) {
            addToast({
              place: "tr",
              type: "success",
              icon: "tim-icons icon-check-2",
              title: "Solicitação enviada",
              description: `${qtd} ${qtd === 1 ? "transportadora notificada" : "transportadoras notificadas"}.`,
            });
          } else if (semEmail > 0) {
            addToast({
              place: "tr",
              type: "warning",
              icon: "tim-icons icon-alert-circle-exc",
              title: "Sem contato cadastrado",
              description: "Transportadoras sem email do tipo \"Cotação\" cadastrado. Acione o comercial.",
            });
          } else {
            addToast({
              place: "tr",
              type: "info",
              icon: "tim-icons icon-alert-circle-exc",
              title: "Rota já coberta",
              description: "Todas as transportadoras do contrato já cobrem essa rota.",
            });
          }
        }}
        onError={(msg) =>
          addToast({
            place: "tr",
            type: "danger",
            icon: "tim-icons icon-alert-circle-exc",
            title: "Falha no envio",
            description: msg,
          })
        }
      />
    </div>
  );
};

export default NovaCotacaoManual;
